import React from 'react';
import { format } from 'date-fns';
import { Plus, X } from 'lucide-react';

/**
 * The pieces every eval form is built from — the create page, the step
 * panel, the eval's settings and the simple form — so a section, a labelled
 * field, an inline number and an add / remove button look the same in all
 * of them instead of drifting per file.
 *
 *   Section   a titled block: 12px semibold title, the explanation on hover
 *   Field     one control with a small muted label above it
 *   numInput  an inline number inside a sentence row ("the last [7] days")
 *   AddButton / RemoveButton   + Add … / × at the end of a row
 */

/**
 * `hint` is the explanation. It lives on the title as a tooltip, not as a
 * line of prose under it: a panel you have to read before you can use it is
 * in the way, and the words are still there the one time someone wants them.
 */
export function Section({ title, hint, aside = null, children, className = 'mb-5' }) {
  return (
    <section className={className}>
      {(title || aside) && (
        <div className="mb-1.5 flex min-h-[20px] items-center justify-between gap-2">
          {title ? (
            <h3 title={hint || undefined}
              className={`text-[12px] font-semibold text-[color:var(--text-primary)] ${hint ? 'cursor-help decoration-dotted decoration-[color:var(--text-disabled)] underline-offset-4 hover:underline' : ''}`}>
              {title}
            </h3>
          ) : <span />}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

/** A label above one control. A div, not a <label>: several of the controls are buttons. */
export function Field({ label, hint, children, className = '' }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="mb-1 text-[12px] text-[color:var(--text-muted)]" title={hint || undefined}>{label}</div>
      {children}
    </div>
  );
}

/** A sentence with controls in it: "Passes when the score is from [0.7] to [1]". */
export const inlineRow = 'flex flex-wrap items-center gap-2 text-[12px] text-[color:var(--text-secondary)]';

/**
 * Inline controls are NOT the global `.input`: it carries `width: 100%` and
 * is declared after `@tailwind utilities` at the same specificity, so it
 * beats `w-20` and the box eats the whole line. These are Tailwind-only.
 * h-8, the height of the dropdowns and segmented controls beside them.
 */
const box = 'h-8 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none placeholder:text-[color:var(--text-muted)] focus:border-[color:var(--border-strong)] disabled:opacity-60';
/** No width of its own: for a text box with a fixed width beside other controls. */
export const inputBox = box;
export const numInput = `${box} w-20 shrink-0 tabular-nums`;
export const textInput = `${box} w-full min-w-0`;

/** "+ Add a range", "+ Add condition": the same quiet button at the end of every list. */
export const AddButton = React.forwardRef(function AddButton({ children, onClick, disabled = false, title, className = '', ...rest }, ref) {
  return (
    <button ref={ref} type="button" onClick={onClick} disabled={disabled} title={title} {...rest}
      className={`-ml-1.5 inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-[12px] font-medium text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-50 disabled:hover:bg-transparent ${className}`}>
      <Plus size={13} aria-hidden />
      {children}
    </button>
  );
});

/** The × at the end of a row. `label` is its accessible name. */
export function RemoveButton({ label, onClick, disabled = false, title }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={title || label}
      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--feedback-error)] disabled:opacity-50">
      <X size={14} aria-hidden />
    </button>
  );
}

/**
 * The code and prompt editors' frame, and Monaco's font. The app's font
 * stack maps `font-mono` to Inter, so code names its monospace explicitly —
 * the same stack and size as the prompt and workflow editors.
 */
export const codeFrame = 'overflow-hidden rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] focus-within:border-[color:var(--border-strong)]';
export const CODE_FONT = 'JetBrains Mono, Menlo, Consolas, monospace';

/**
 * An absolute time in the app's one format (dd-MM-yyyy HH:mm:ss), the same as
 * runCells' formatDateTime. Not imported from there: runCells pulls in the
 * markdown renderer, which the editor's tests cannot load.
 */
export function formatDateTime(iso) {
  if (!iso) return '';
  try { return format(new Date(iso), 'dd-MM-yyyy HH:mm:ss'); } catch (_) { return String(iso); }
}
